import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { UserAvatar } from './KanbanListView';

/**
 * Multi-select autocomplete for the kanban "Assigné à" filter.
 *
 * Replaces the old click-on-avatars UX: type to search every project member,
 * pick several, each appears as a removable chip (avatar + name + ×).
 *
 * `value` is an array of String(user.id); `onChange(nextValue)` is called with
 * the updated array. Matching a task to a selected user is done by KanbanBoard
 * (it resolves a task's assignee — id *or* name — to a user id).
 */
export default function KanbanAssigneeFilter({ users = [], value = [], onChange }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selectedSet = new Set(value.map(String));
  const selectedUsers = users.filter((u) => selectedSet.has(String(u.id)));

  const q = search.trim().toLowerCase();
  const suggestions = users.filter((u) =>
    !selectedSet.has(String(u.id)) &&
    (q === '' ||
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q))
  );

  const addUser = (u) => {
    onChange([...value, String(u.id)]);
    setSearch('');
    inputRef.current?.focus(); // keep focus to chain multiple selections
  };
  const removeUser = (id) => onChange(value.filter((v) => String(v) !== String(id)));
  const clearAll = () => onChange([]);

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-slate-500 whitespace-nowrap">Assigné à :</span>

      <div ref={ref} className="relative">
        {/* Input box hosting the chips + the search field */}
        <div
          onClick={() => { setOpen(true); inputRef.current?.focus(); }}
          className="flex flex-wrap items-center gap-1 min-w-[14rem] max-w-[26rem] px-2 py-1 border border-slate-300 rounded-lg bg-white cursor-text focus-within:ring-2 focus-within:ring-iris-500 focus-within:border-iris-500"
        >
          {selectedUsers.map((u) => (
            <span
              key={u.id}
              className="tag tag-iris pl-0.5 pr-1 py-0.5"
            >
              <UserAvatar name={u.name} users={users} size="sm" />
              <span className="font-medium max-w-[8rem] truncate">{u.name}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeUser(u.id); }}
                className="text-iris-400 hover:text-iris-700 rounded-full"
                aria-label={`Retirer ${u.name}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              // Backspace on empty input removes the last chip.
              if (e.key === 'Backspace' && search === '' && selectedUsers.length) {
                removeUser(selectedUsers[selectedUsers.length - 1].id);
              }
            }}
            placeholder={selectedUsers.length ? 'Ajouter…' : 'Rechercher une personne…'}
            className="flex-1 min-w-[6rem] border-0 p-0.5 text-sm bg-transparent focus:ring-0 focus:outline-none"
          />
          <ChevronDown size={14} className="text-slate-400 shrink-0" />
        </div>

        {/* Suggestions dropdown */}
        {open && (
          <div className="absolute z-30 left-0 mt-1 w-72 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg py-1">
            {suggestions.length ? (
              suggestions.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => addUser(u)}
                  className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm hover:bg-iris-50 transition-colors"
                >
                  <UserAvatar name={u.name} users={users} />
                  <span className="min-w-0">
                    <span className="block font-medium text-slate-900 truncate">{u.name}</span>
                    {u.email && <span className="block text-xs text-slate-500 truncate">{u.email}</span>}
                  </span>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-slate-500 italic">
                {users.length ? 'Aucune personne trouvée' : 'Aucun membre'}
              </div>
            )}
          </div>
        )}
      </div>

      {value.length > 0 && (
        <button
          type="button"
          onClick={clearAll}
          className="text-xs text-iris-600 hover:text-iris-800 underline whitespace-nowrap"
        >
          Effacer ({value.length})
        </button>
      )}
    </div>
  );
}
